import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { getThesisPlan, planExpiresAtIso, type ThesisPlanId } from '@/constants/thesisPlans'
import {
  fetchMe,
  isThesisCloudAuth,
  loginAccount,
  registerAccount,
  type ThesisApiUser,
} from '@/lib/thesisApi'
import { syncThesisAuthToConnection } from '@/lib/thesisPlatform'

const STORAGE_KEY = 'thesis-subscription-v3'

export type ThesisSubscription = {
  planId: ThesisPlanId
  startedAt: string
  /** null = Token 不过期（用完再续） */
  expiresAt: string | null
  remainingTokens: number
  orderId: string
}

type Profile = {
  displayName: string
  email: string
}

type State = {
  profile: Profile
  sub: ThesisSubscription | null
  /** 云端会话；空则本机演示模式 */
  token: string
  /** 注册（云端：验证码；本机：直接赠送） */
  register: (args: {
    email: string
    password?: string
    emailCode?: string
    displayName?: string
  }) => Promise<void>
  login: (args: { email: string; password: string }) => Promise<void>
  /** @deprecated 用 register/login；保留给无 API 演示 */
  signIn: (email: string, displayName?: string) => void
  signOut: () => void
  refreshMe: () => Promise<void>
  applyCloudUser: (token: string, user: ThesisApiUser) => void
  purchase: (planId: ThesisPlanId) => ThesisSubscription
  setProfile: (patch: Partial<Profile>) => void
  deductTokens: (tokens: number) => number
}

function makeOrderId(planId: string): string {
  return `TH-${planId}-${Date.now().toString(36).toUpperCase()}`
}

function giftSub(): ThesisSubscription {
  const plan = getThesisPlan('trial')
  const now = new Date()
  return {
    planId: 'trial',
    startedAt: now.toISOString(),
    expiresAt: planExpiresAtIso(plan.days, now),
    remainingTokens: plan.quotaWan * 10_000,
    orderId: makeOrderId('trial'),
  }
}

function mapCloudSub(sub: ThesisApiUser['sub']): ThesisSubscription | null {
  if (!sub) return null
  return {
    planId: sub.planId as ThesisPlanId,
    startedAt: sub.startedAt,
    expiresAt: sub.expiresAt,
    remainingTokens: sub.remainingTokens,
    orderId: sub.orderId,
  }
}

export const useSubscriptionStore = create<State>()(
  persist(
    (set, get) => ({
      profile: { displayName: '', email: '' },
      sub: null,
      token: '',
      applyCloudUser: (token, user) => {
        set({
          token,
          profile: { email: user.email, displayName: user.displayName },
          sub: mapCloudSub(user.sub),
        })
        syncThesisAuthToConnection(token)
      },
      register: async ({ email, password, emailCode, displayName }) => {
        if (isThesisCloudAuth()) {
          if (!password || !emailCode) throw new Error('请填写验证码与密码')
          const r = await registerAccount({ email, password, emailCode, displayName })
          if (!r.ok) throw new Error(r.error)
          get().applyCloudUser(r.data.token, r.data.user)
          return
        }
        get().signIn(email, displayName)
      },
      login: async ({ email, password }) => {
        if (isThesisCloudAuth()) {
          const r = await loginAccount({ email, password })
          if (!r.ok) throw new Error(r.error)
          get().applyCloudUser(r.data.token, r.data.user)
          return
        }
        get().signIn(email)
      },
      signIn: (email, displayName) => {
        const trimmed = email.trim().toLowerCase()
        if (!trimmed) return
        const name = displayName?.trim() || trimmed.split('@')[0] || '用户'
        const hadSub = !!get().sub
        set({
          profile: { email: trimmed, displayName: name },
          sub: hadSub ? get().sub : giftSub(),
          token: '',
        })
        syncThesisAuthToConnection('')
      },
      signOut: () => {
        set({ profile: { displayName: '', email: '' }, sub: null, token: '' })
        syncThesisAuthToConnection('')
      },
      refreshMe: async () => {
        const token = get().token
        if (!token || !isThesisCloudAuth()) return
        const r = await fetchMe(token)
        if (!r.ok) {
          if (r.status === 401) get().signOut()
          return
        }
        get().applyCloudUser(token, r.data.user)
      },
      purchase: (planId) => {
        const plan = getThesisPlan(planId)
        const now = new Date()
        const next: ThesisSubscription = {
          planId,
          startedAt: now.toISOString(),
          expiresAt: planExpiresAtIso(plan.days, now),
          remainingTokens: plan.quotaWan * 10_000,
          orderId: makeOrderId(planId),
        }
        set({ sub: next })
        return next
      },
      setProfile: (patch) => set({ profile: { ...get().profile, ...patch } }),
      deductTokens: (tokens) => {
        const sub = get().sub
        if (!sub || tokens <= 0) return 0
        const take = Math.min(sub.remainingTokens, Math.ceil(tokens))
        set({
          sub: { ...sub, remainingTokens: Math.max(0, sub.remainingTokens - take) },
        })
        // 云端生成由服务端扣额度；此处只更新本地展示，随后 refreshMe 对齐
        const token = get().token
        if (token && isThesisCloudAuth()) {
          void get().refreshMe()
        }
        return take
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        profile: s.profile,
        sub: s.sub,
        token: s.token,
      }),
    },
  ),
)

export function isSignedIn(profile: Profile): boolean {
  return !!profile.email?.trim()
}

export function formatQuotaWan(tokens: number): string {
  const wan = tokens / 10_000
  if (wan >= 100) return `${Math.round(wan)} 万`
  return `${wan.toFixed(1).replace(/\.0$/, '')} 万`
}

export function isSubActive(sub: ThesisSubscription | null): boolean {
  if (!sub || !(sub.remainingTokens > 0)) return false
  if (!sub.expiresAt) return true
  return new Date(sub.expiresAt).getTime() > Date.now()
}

